import { Authenticator, type AuthenticatorProps } from '@aws-amplify/ui-react';
import { rememberDevice } from 'aws-amplify/auth';
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router';
import { logger } from '@/utils/logger';
import '@aws-amplify/ui-react/styles.css';

let pendingRememberDevice = false;

const formFields: AuthenticatorProps['formFields'] = {
  signIn: {
    username: {
      label: 'Email',
      placeholder: 'Enter your email address',
      isRequired: true,
    },
  },
  forceNewPassword: {
    password: {
      label: 'New Password',
      placeholder: 'Create a permanent password',
      isRequired: true,
    },
  },
  confirmSignIn: {
    confirmation_code: {
      label: 'Verification Code',
      placeholder: 'Enter the 6-digit code from your authenticator app',
      isRequired: true,
    },
  },
  setupTotp: {
    QR: {
      totpIssuer: 'Voltasis',
    },
    confirmation_code: {
      label: 'Verification Code',
      placeholder: 'Enter the 6-digit code from your authenticator app',
      isRequired: true,
    },
  },
};

function Header() {
  return (
    <div className="mb-6 text-center">
      <h1 className="flex items-center justify-center gap-2 text-2xl font-bold text-surface-900">
        <svg className="h-8 w-8" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16" cy="16" r="15" fill="#0d9488" />
          <line x1="16" y1="2.5" x2="16" y2="5.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.45" />
          <line x1="29.5" y1="16" x2="26.5" y2="16" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.45" />
          <line x1="16" y1="29.5" x2="16" y2="26.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.45" />
          <line x1="2.5" y1="16" x2="5.5" y2="16" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.45" />
          <path d="M11 6.5L16 17L21 6.5" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="16" cy="17" r="2" fill="white" />
        </svg>
        Voltasis
      </h1>
      <p className="mt-1 text-sm text-surface-500">Professional Time Tracking</p>
    </div>
  );
}

function ConfirmSignInHeader() {
  return (
    <div className="px-8 pt-6 pb-2">
      <h2 className="text-lg font-semibold text-surface-900 dark:text-white">Two-factor authentication</h2>
      <p className="mt-1 text-sm text-surface-500 dark:text-surface-400">
        Enter the verification code from your authenticator app.
      </p>
    </div>
  );
}

function SetupTotpHeader() {
  return (
    <div className="px-8 pt-4 pb-2">
      <h2 className="text-lg font-semibold text-surface-900 dark:text-white">Set up two-factor authentication</h2>
      <div className="mt-3 space-y-2 text-sm text-surface-600 dark:text-surface-300">
        <p>
          <span className="font-medium text-surface-900 dark:text-white">Step 1:</span> Download an authenticator
          app such as Google Authenticator, Authy, or Microsoft Authenticator.
        </p>
        <p>
          <span className="font-medium text-surface-900 dark:text-white">Step 2:</span> Scan the QR code below with
          your authenticator app.
        </p>
        <p>
          <span className="font-medium text-surface-900 dark:text-white">Step 3:</span> Enter the 6-digit
          verification code from your app.
        </p>
      </div>
    </div>
  );
}

function ForceNewPasswordHeader() {
  return (
    <div className="px-8 pt-6 pb-2">
      <h2 className="text-lg font-semibold text-surface-900 dark:text-white">Set a new password</h2>
      <p className="mt-1 text-sm text-surface-500 dark:text-surface-400">
        Your temporary password has expired. Please create a permanent password.
      </p>
    </div>
  );
}

function RememberDeviceCheckbox() {
  const [checked, setChecked] = useState(false);

  return (
    <div className="px-8 pb-4">
      <label className="flex cursor-pointer items-center gap-2.5 text-sm text-surface-600 dark:text-surface-400">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => {
            setChecked(e.target.checked);
            pendingRememberDevice = e.target.checked;
          }}
          className="h-4 w-4 rounded border-surface-300 dark:border-surface-600 accent-primary-600"
        />
        Remember this device for 30 days
      </label>
    </div>
  );
}

function AuthenticatedOutlet() {
  useEffect(() => {
    if (pendingRememberDevice) {
      pendingRememberDevice = false;
      rememberDevice().catch((error: unknown) => {
        logger.warn('Failed to remember device', { error: String(error) });
      });
    }
  }, []);

  return <Outlet />;
}

export function RequireAuth() {
  return (
    <Authenticator
      hideSignUp
      formFields={formFields}
      components={{
        Header,
        ConfirmSignIn: {
          Header: ConfirmSignInHeader,
          Footer: RememberDeviceCheckbox,
        },
        SetupTotp: {
          Header: SetupTotpHeader,
          Footer: RememberDeviceCheckbox,
        },
        ForceNewPassword: { Header: ForceNewPasswordHeader },
      }}
    >
      <AuthenticatedOutlet />
    </Authenticator>
  );
}
